import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICreateSim, ISim, IUpdateSim, Operador } from 'modelos/src';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { SimsService } from '../../../../../auxiliares/servicios/http/sims.service';
import { LoginService } from '../../../../login/login.service';
import { SimConfigComponent } from '../../../alarmas/alarmas-domiciliarias/crear-alarma-domiciliaria-completo/sim-config/sim-config.component';

export interface ICrearEditarSimData {
  edit?: ISim;
}

/**
 * Alta y edición de una SIM suelta, sin equipo. Los campos y el autocompletado
 * de APN por operador son los de `app-sim-config` (el mismo bloque que usan
 * los formularios de tracker y alarma).
 */
@Component({
  selector: 'app-crear-editar-sim',
  standalone: true,
  imports: [AuxiliaresModule, SimConfigComponent],
  templateUrl: './crear-editar-sim.component.html',
  styleUrl: './crear-editar-sim.component.scss',
})
export class CrearEditarSimComponent implements OnInit {
  helper = inject(HelperService);
  private service = inject(SimsService);
  data = inject<ICrearEditarSimData>(MAT_DIALOG_DATA, { optional: true });
  dialogRef = inject<MatDialogRef<CrearEditarSimComponent>>(MatDialogRef);

  public form?: FormGroup;
  public guardando = false;
  public isEditing = false;
  public title = 'Crear SIM';
  public mostrarCampoCliente = HelperService.getTipoCliente() !== 'Final';

  ngOnInit(): void {
    const sim = this.data?.edit;
    this.isEditing = !!sim?._id;
    this.title = this.isEditing
      ? `Editar SIM ${SimsService.nombre(sim)}`
      : 'Crear SIM';

    const clienteDefault = this.mostrarCampoCliente
      ? sim?.idCliente
      : LoginService.getCliente()?._id;

    this.form = new FormGroup({
      iccid: new FormControl(sim?.iccid),
      numero: new FormControl(sim?.numero),
      operador: new FormControl<Operador | undefined>(sim?.operador),
      apn: new FormControl(sim?.apn),
      usuario: new FormControl(sim?.usuario),
      password: new FormControl(sim?.password),
      idCliente: new FormControl(clienteDefault),
    });
  }

  /** Sin ICCID ni número la SIM no se puede identificar en ningún listado. */
  public get valido(): boolean {
    const v = this.form?.value;
    return !!(v?.iccid?.trim() || v?.numero?.trim()) && !!v?.idCliente;
  }

  private getData(): ICreateSim | IUpdateSim {
    const v = this.form!.value;
    const limpio = (x?: string | null) => x?.trim() || undefined;
    return {
      iccid: limpio(v.iccid),
      numero: limpio(v.numero),
      operador: v.operador || undefined,
      apn: limpio(v.apn),
      usuario: v.usuario ?? undefined,
      password: v.password ?? undefined,
      idCliente: v.idCliente || undefined,
    };
  }

  public async guardar(): Promise<void> {
    if (!this.valido || this.guardando) return;
    this.guardando = true;
    try {
      const data = this.getData();
      if (this.isEditing) {
        await this.service.update(this.data!.edit!._id!, data);
        this.helper.notifSuccess('SIM actualizada');
      } else {
        await this.service.create(data);
        this.helper.notifSuccess('SIM creada');
      }
      this.dialogRef.close(true);
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.guardando = false;
    }
  }

  public cerrar(): void {
    this.dialogRef.close();
  }
}
